import React, { useState, useEffect } from "react";
import PageHeader from "../components/shared/PageHeader";
import PageContainer from "../components/shared/PageContainer";
import {
  Alert,
  Button,
  Drawer,
  Spinner,
  TextInput,
  Td,
  Th,
  Tr,
} from "../components/ui";
import { entityApi } from "../services/api";

interface Client {
  id: number;
  businessName: string;
  businessDisplayName?: string;
  businessAddress?: string;
  businessPostcode?: string;
  createdAt?: string;
}

const CreateClient: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [showViewOffcanvas, setShowViewOffcanvas] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [viewingClient, setViewingClient] = useState<Client | null>(null);

  // Form State
  const [formBusinessName, setFormBusinessName] = useState("");
  const [formDisplayName, setFormDisplayName] = useState("");
  const [formAddress, setFormAddress] = useState("");
  const [formPostcode, setFormPostcode] = useState("");

  const fetchClients = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await entityApi.getAllClients();
      setClients(data);
    } catch (err: any) {
      setError("Failed to fetch clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const resetForm = () => {
    setEditingClient(null);
    setFormBusinessName("");
    setFormDisplayName("");
    setFormAddress("");
    setFormPostcode("");
  };

  const handleOpenAdd = () => {
    resetForm();
    setShowOffcanvas(true);
  };

  const handleOpenEdit = (client: Client) => {
    resetForm();
    setEditingClient(client);
    setFormBusinessName(client.businessName);
    setFormDisplayName(client.businessDisplayName || "");
    setFormAddress(client.businessAddress || "");
    setFormPostcode(client.businessPostcode || "");
    setShowOffcanvas(true);
  };

  const handleOpenView = (client: Client) => {
    setViewingClient(client);
    setShowViewOffcanvas(true);
  };

  const handleClose = () => {
    setShowOffcanvas(false);
  };

  const handleCloseView = () => {
    setShowViewOffcanvas(false);
    setViewingClient(null);
  };

  const handleDelete = async (client: Client) => {
    if (!window.confirm(`Are you sure you want to delete "${client.businessName}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await entityApi.deleteClient({ id: client.id });
      fetchClients();
    } catch (err) {
      alert("Failed to delete client.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formBusinessName) {
      alert("Please fill in all required fields.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        id: editingClient?.id || 0,
        businessName: formBusinessName,
        businessDisplayName: formDisplayName,
        businessAddress: formAddress,
        businessPostcode: formPostcode,
      };

      if (editingClient) {
        await entityApi.updateClient(payload);
      } else {
        await entityApi.createClient(payload);
      }

      setShowOffcanvas(false);
      fetchClients();
    } catch (err) {
      alert("Failed to save client.");
    } finally {
      setSubmitting(false);
    }
  };

  const formId = "client-form";

  return (
    <PageContainer>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <PageHeader title="Manage Clients" subTitle="Clients" />
        <Button variant="primary" onClick={handleOpenAdd} className="shrink-0">
          <i className="bi bi-building" />
          Create Branch
        </Button>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      <section className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <Th>Business Name</Th>
                <Th>Display Name</Th>
                <Th>Business Address</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center">
                    <Spinner className="mx-auto" />
                  </td>
                </tr>
              ) : clients.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-12 text-center text-sm text-zinc-500"
                  >
                    No clients found.
                  </td>
                </tr>
              ) : (
                clients.map((client) => (
                  <Tr key={client.id}>
                    <Td className="font-medium text-zinc-900">
                      {client.businessName}
                    </Td>
                    <Td className="text-zinc-500">
                      {client.businessDisplayName || "—"}
                    </Td>
                    <Td className="text-zinc-500">
                      {client.businessAddress || "—"}
                    </Td>
                    <Td className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          variant="ghost"
                          onClick={() => handleOpenView(client)}
                          className="px-3 py-1.5 text-xs"
                        >
                          <i className="bi bi-eye" />
                          View
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleOpenEdit(client)}
                          className="px-3 py-1.5 text-xs"
                        >
                          <i className="bi bi-pencil" />
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => handleDelete(client)}
                          className="px-3 py-1.5 text-xs"
                        >
                          <i className="bi bi-trash" />
                          Delete
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Side panel for Create/Edit Client */}
      <Drawer
        open={showOffcanvas}
        onOpenChange={(open) => !open && handleClose()}
        title={editingClient ? "Edit Client" : "Create New Client"}
        footer={
          <>
            <Button variant="outline" onClick={handleClose} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              form={formId}
              variant="primary"
              disabled={submitting}
              className="flex-1"
            >
              {submitting ? (
                <>
                  <Spinner size="size-4" />
                  Saving…
                </>
              ) : editingClient ? (
                "Update Client"
              ) : (
                "Create Client"
              )}
            </Button>
          </>
        }
      >
        <form id={formId} onSubmit={handleSubmit} className="space-y-4">
          <TextInput
            label="Business Name *"
            type="text"
            placeholder="e.g. Sweet Treats Bakery"
            value={formBusinessName}
            onChange={(e) => setFormBusinessName(e.target.value)}
            required
          />

          <TextInput
            label="Business Display Name"
            type="text"
            placeholder="e.g. Sweet Treats"
            value={formDisplayName}
            onChange={(e) => setFormDisplayName(e.target.value)}
          />

          <TextInput
            label="Business Address"
            type="text"
            placeholder="e.g. 45 Baker Street, London"
            value={formAddress}
            onChange={(e) => setFormAddress(e.target.value)}
          />

          <TextInput
            label="Business Postcode"
            type="text"
            placeholder="e.g. NW1 6XE"
            value={formPostcode}
            onChange={(e) => setFormPostcode(e.target.value)}
          />
        </form>
      </Drawer>

      {/* Side panel for Client details */}
      <Drawer
        open={showViewOffcanvas}
        onOpenChange={(open) => !open && handleCloseView()}
        title="Client Details"
        footer={
          <>
            <Button variant="outline" onClick={handleCloseView} className="flex-1">
              Close
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={() => {
                const client = viewingClient;
                handleCloseView();
                if (client) handleOpenEdit(client);
              }}
            >
              <i className="bi bi-pencil" />
              Edit
            </Button>
          </>
        }
      >
        {viewingClient && (
          <dl className="space-y-5">
            {[
              { label: "Business Name", value: viewingClient.businessName },
              {
                label: "Display Name",
                value: viewingClient.businessDisplayName || "—",
              },
              {
                label: "Business Address",
                value: viewingClient.businessAddress || "—",
              },
              {
                label: "Business Postcode",
                value: viewingClient.businessPostcode || "—",
              },
              ...(viewingClient.createdAt
                ? [
                    {
                      label: "Created At",
                      value: new Date(
                        viewingClient.createdAt
                      ).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      }),
                    },
                  ]
                : []),
            ].map(({ label, value }) => (
              <div key={label}>
                <dt className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
                  {label}
                </dt>
                <dd className="mt-1 text-sm text-zinc-900">{value}</dd>
              </div>
            ))}
          </dl>
        )}
      </Drawer>
    </PageContainer>
  );
};

export default CreateClient;
